import { MutationFunctionOptions, MutationHookOptions, MutationResult } from "@apollo/client";
import { useMutation } from "../../../hooks";
import { FetchResult } from "@apollo/client/link/core";
import { addToMatterExclusionsCache } from "../../query";
import { newClientId } from "../../client/client-id-generator";
import {
  CreateExclusionsMutation,
  CreateExclusionsMutationVariables,
  ExclusionCreateInput,
} from "~/gql/graphql";
import { graphql } from "~/gql";

export const CREATE_EXCLUSIONS = graphql(/* GraphQL */ `
  mutation createExclusions($input: [ExclusionCreateInput!]!) {
    createExclusions(input: $input) {
      ...ExclusionFragment
    }
  }
`);

export type CreateExclusionsMutationFunction = (
  matterId: number,
  exclusions: ExclusionCreateInput | ExclusionCreateInput[],
  options?: MutationFunctionOptions<CreateExclusionsMutation, CreateExclusionsMutationVariables>
) => Promise<FetchResult<CreateExclusionsMutation>>;

export function useCreateExclusionsMutation(
  options?: MutationHookOptions<CreateExclusionsMutation, CreateExclusionsMutationVariables>,
  order?: number
): [CreateExclusionsMutationFunction, MutationResult<CreateExclusionsMutation>] {
  const [mutationFunction, mutationResult] = useMutation(CREATE_EXCLUSIONS, {
    errorPolicy: "none",
    ...(options || {}),
  });
  const createExclusionsMutation: CreateExclusionsMutationFunction = (
    matterId,
    inputs,
    options
  ) => {
    const exclusions = Array.isArray(inputs) ? inputs : [inputs];
    const optimisticExclusions = exclusions.map((e, i) => ({
      ...e,
      id: newClientId(),
      order: order || -1,
      autoFocus: i === exclusions.length - 1 ? true : false,
      matterId,
      __typename: "Exclusion",
      fromLibraryExclusionId: null,
    })) as CreateExclusionsMutation["createExclusions"];
    return mutationFunction({
      variables: { input: exclusions },
      optimisticResponse: {
        createExclusions: optimisticExclusions,
      },
      update(cache, { data }) {
        if ((data?.createExclusions?.length || 0) > 0) {
          addToMatterExclusionsCache(cache, matterId, data!.createExclusions);
        }
      },
      ...(options || {}),
    });
  };
  return [createExclusionsMutation, mutationResult];
}
